<template>
  <div>
    <h1 class="page-header">{{ $t('promotion.promotion') }}</h1>
    <div class="row">
      <div class="col-md-3 col-lg-3 col-sm-12">
        <ts-card>
          <ts-card-wrapper>
            <div class="form-group m-b-15">
              <label class="required">{{ $t('promotion.mainProgram') }}</label>
              <ts-search-select
                v-model="model.acp_id"
                :options="mainPrograms"
                @change="mainProgramChange(model.acp_id)"
              ></ts-search-select>
            </div>
            <div class="form-group m-b-15">
              <label>
                {{ $t('promotion.subProgram') }}
                <span class="text-danger">*</span>
                <i class="fas fa-spinner spin" v-if="main_program_change"></i>
              </label>
              <ts-search-select
                v-model="model.acps_id"
                :options="subPrograms"
                :disabled="main_program_change"
                @change="subProgramChange(model.acps_id)"
              ></ts-search-select>
              <div class="validate" v-if="errors.has('acps_id')">
                {{ errors.first('acps_id') }}
              </div>
            </div>
            <div class="form-group m-b-15">
              <label class="form-col-label">
                {{ $t('promotion.academicTerm') }}
                <span class="text-danger">*</span>
                <i class="fas fa-spinner spin" v-if="sub_program_change"></i>
              </label>
              <a-select
                v-model="model.ac_term_id"
                mode="multiple"
                :options="acTerms"
                :disabled="sub_program_change"
                @change="selectedTerm"
                style="width:100%"
              ></a-select>
              <div class="validate" v-if="errors.has('ac_term_id')">
                {{ errors.first('ac_term_id') }}
              </div>
            </div>
            <div class="form-group m-b-15">
              <label class="required">{{ $t('promotion.rate') }}</label>
              <input
                v-model.number="model.promotion_rate"
                type="number"
                step="any"
                class="form-control"
                :class="{ 'is-invalid': errors.has('promotion_rate') }"
                :placeholder="$t('promotion.enterpromotionrate')"
              />
              <div class="invalid-feedback" v-if="errors.has('promotion_rate')">
                {{ errors.first('promotion_rate') }}
              </div>
            </div>
            <div class="form-group m-b-15">
              <label class="required">{{ $t('promotion.startDate') }}</label>
              <ts-date-picker-input
                v-model="model.promotion_start_date"
              ></ts-date-picker-input>
            </div>
            <div class="form-group m-b-15">
              <label class="required">{{ $t('promotion.expiryDate') }}</label>
              <ts-date-picker-input
                v-model="model.promotion_expiry_date"
              ></ts-date-picker-input>
              <div class="validate" v-if="errors.has('promotion_expiry_date')">
                {{ errors.first('promotion_expiry_date') }}
              </div>
            </div>
            <div class="form-group m-b-15">
              <label class="form-col-label">{{ $t('promotion.remark') }}</label>
              <textarea
                v-model="model.remark"
                rows="3"
                class="form-control"
                :class="{ 'is-invalid': errors.has('remark') }"
                :placeholder="$t('promotion.leaveyourremarkhere')"
              ></textarea>
              <div class="invalid-feedback" v-if="errors.has('remark')">
                {{ errors.first('remark') }}
              </div>
            </div>
            <div class="form-group m-b-15">
              <label class="form-col-label">{{
                $t('promotion.description')
              }}</label>
              <VueEditor
                v-model="model.app_promotion_detail"
                :editor-toolbar="customToolbar"
                useCustomImageHandler
              />
              <div class="invalid-feedback" v-if="errors.has('remark')">
                {{ errors.first('remark') }}
              </div>
            </div>
            <div class="text-right">
              <ts-button v-if="updating" class="mr-2" @click.prevent="cancel">
                {{ $t('cancel') }}
              </ts-button>
              <ts-button
                v-if="updating"
                class="btn-primary"
                :waiting="waiting"
                @click.prevent="update"
              >
                {{ $t('update') }}
              </ts-button>
              <ts-button
                v-if="!updating"
                :waiting="waiting"
                class="btn-primary"
                @click.prevent="store"
                >{{ $t('save') }}</ts-button
              >
            </div>
          </ts-card-wrapper>
        </ts-card>
      </div>
      <div class="col-md-9 col-lg-9 col-sm-12">
        <data-table :fetch="fetch" @fetched="fetch = false"></data-table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import DataTable from './table'
import { VueEditor } from 'vue2-editor'
import moment from 'moment'

export default {
  name: 'promotion-form',
  components: {
    DataTable,
    VueEditor
  },
  data() {
    return {
      model: {
        promotion_id: '',
        acp_id: '',
        acps_id: '',
        ac_term_id: undefined,
        promotion_rate: '',
        promotion_start_date: moment().format('DD-MM-YYYY'),
        promotion_expiry_date: moment().format('DD-MM-YYYY'),
        remark: '',
        app_promotion_detail: ''
      },
      errors: new Errors(),
      waiting: false,
      fetch: false,
      updating: false,
      main_program_change: false,
      sub_program_change: false,
      customToolbar: [
        [{ font: [] }],
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' }
        ],
        [{ header: 1 }, { header: 2 }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ color: [] }, { background: [] }],
        ['link'],
        [{ direction: 'rtl' }],
        ['clean']
      ]
    }
  },
  created() {
    this.setEditData()
  },
  computed: {
    ...mapState('promotion', ['edit_data']),
    mainPrograms() {
      return this.$store.state.promotion.main_programs.map(el => ({
        value: el.acp_id,
        text: el.acp_name_foreign
      }))
    },
    subPrograms() {
      return this.$store.state.promotion.sub_programs.map(el => ({
        value: el.acps_id,
        text: el.acps_name_foreign
      }))
    },
    acTerms() {
      return this.$store.state.promotion.ac_terms.map(el => ({
        value: el.ac_term_id,
        label: el.term_name
      }))
    }
  },
  methods: {
    store() {
      this.errors = new Errors()
      this.waiting = true
      this.$store
        .dispatch('promotion/store', this.model)
        .then(response => {
          this.clearInput()
          this.$toasted.success(response.message)
          this.waiting = false
          this.fetch = true
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
          this.waiting = false
        })
    },
    update() {
      this.errors = new Errors()
      this.waiting = true
      this.$store
        .dispatch('promotion/update', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.waiting = false
          this.fetch = true
          this.cancel()
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
          this.waiting = false
        })
    },
    async mainProgramChange(acp_id) {
      this.main_program_change = true
      this.model.acps_id = ''
      this.model.ac_term_id = undefined
      await this.$store.dispatch('promotion/getSubProgram', acp_id)
      this.main_program_change = false
    },
    async subProgramChange(acps_id) {
      this.sub_program_change = true
      this.model.ac_term_id = undefined
      await this.$store.dispatch('promotion/getTerm', acps_id)
      this.sub_program_change = false
    },
    cancel() {
      this.updating = false
      this.$store.commit('promotion/SET_EDIT_DATA', {})
      this.$router.push({ name: 'promotion' })
      this.clearInput()
    },
    clickBackURL() {
      this.$store.commit('promotion/SET_EDIT_DATA', {})
      this.clearInput()
      this.fetch = true
      this.updating = false
    },
    async setEditData() {
      if (!_.isEmpty(this.edit_data)) {
        this.errors = new Errors()
        await this.mainProgramChange(this.edit_data.sub_program.acp_id)
        await this.subProgramChange(this.edit_data.acps_id)
        this.model = {
          promotion_id: this.edit_data.promotion_id,
          acp_id: this.edit_data.sub_program.acp_id,
          acps_id: this.edit_data.acps_id,
          ac_term_id: [this.edit_data.ac_term.ac_term_id],
          promotion_rate: this.edit_data.promotion_rate,
          promotion_start_date: this.edit_data.promotion_start_date,
          promotion_expiry_date: this.edit_data.promotion_expiry_date,
          remark: this.edit_data.remark,
          app_promotion_detail: this.edit_data.app_promotion_detail
        }
        this.updating = true
      }
    },
    clearInput() {
      this.model = {
        acp_id: '',
        acps_id: '',
        ac_term_id: undefined,
        promotion_rate: '',
        promotion_start_date: moment().format('DD-MM-YYYY'),
        promotion_expiry_date: moment().format('DD-MM-YYYY'),
        remark: '',
        app_promotion_detail: ''
      }
    },
    selectedTerm(value) {
      if (!_.isEmpty(this.edit_data)) {
        this.model.ac_term_id.splice(0, 1)
      }
    }
  },
  watch: {
    edit_data: {
      handler() {
        this.setEditData()
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (_.isEmpty(to.params) && to.name != 'promotion') {
      this.$store.commit('promotion/RESET_STATE')
      this.clickBackURL()
    }
    next()
  }
}
</script>
